import { computed, inject, Injectable, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../core/services/language.service';
import {
	IFilter,
	ITableCell,
	ITableConfig,
	ITableHeader,
	ITableRow,
	ITableSortEvent,
	IToolbar,
} from '@tecalisdev/infinity';
import { MasterService } from '../../../../shared/services/master.service';
import { IUsersListBody } from '../models/users.models';
import { formatDate, notAvailableLabel } from '../../../../core/utils/utils';
import { RowItemAction, MenuItemAction } from '../../../../shared/models/master.models';
import { createDateRangeFilters } from '../../../../shared/list/create-date-range-filters.util';
import { environment } from '../../../../../environments/environment';
import { RolePermissionsService } from '../../../../core/services/role-permissions.service';
import { Permission, UserRole } from '../../../../core/models/role-permissions.models';

@Injectable({
	providedIn: 'root',
})
export class UsersHelperService {
	trans = inject(TranslateService);
	masterService = inject(MasterService);
	private langSvc = inject(LanguageService);
	private rolePermissions = inject(RolePermissionsService);

	readonly toolbar: Signal<IToolbar> = computed<IToolbar>(() => {
		this.langSvc.currentLang(); // dependencia reactiva: recalcula al cambiar idioma
		const filters: IFilter[] = [
			{
				type: 'select',
				title: this.trans.instant('FILTERS.ROLE'),
				name: 'role_uuid',
				placeholder: this.trans.instant('FILTERS.ROLE'),
				options: this.masterService.roles,
			},
			...createDateRangeFilters({
				fromTitle: this.trans.instant('FILTERS.FROM'),
				toTitle: this.trans.instant('FILTERS.TO'),
			}),
		];

		// Si hay más de una compañía, agregar filtro de compañía
		if (this.masterService.companies.length > 1 || this.rolePermissions.isSuperadmin()) {
			// Poner en la posición 2 el filtro de compañía
			filters.splice(1, 0, {
				type: 'select',
				title: this.trans.instant('FILTERS.COMPANY'),
				name: 'companies_uuid',
				placeholder: this.trans.instant('FILTERS.COMPANY'),
				options: this.masterService.companies,
				search: true,
			});
		}
		return {
			filters,
			search: {
				placeholder: this.trans.instant('USERS.LIST.SEARCH'),
				name: 'name',
			},
			...(this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)
				? {
						action: {
							id: MenuItemAction.SHOW_CURL,
							icon: 'icon-code-brackets',
							tooltip: this.trans.instant('GENERAL.CURL_REQUEST'),
						},
					}
				: {}),
		};
	});

	get header(): ITableHeader[] {
		const headers: ITableHeader[] = [
			{ label: this.trans.instant('TABLE.HEADER.NAME') },
			{ label: this.trans.instant('TABLE.HEADER.EMAIL') },
			{ label: this.trans.instant('TABLE.HEADER.ROLE') },
			{ label: this.trans.instant('TABLE.HEADER.COMPANIES') },
		];

		if (this.canViewLastAccess()) {
			headers.push({
				label: this.trans.instant('TABLE.HEADER.DATE_LAST_ACCESS'),
				align: 'right',
			});
		}

		headers.push({
			label: this.trans.instant('TABLE.HEADER.DATE_CREATE_ABREV'),
			sortable: true,
			sortKey: 'created_at',
			align: 'right',
		});

		// Agregar columna de acciones si tiene permiso de editar usuarios
		if (this.rolePermissions.canEditUsers() !== false) {
			headers.push({ label: '' });
		}

		return headers;
	}

	get config(): ITableConfig {
		return {
			clickeable: this.rolePermissions.canEditUsers(),
			pagination: {
				page: 1,
				limit: 25,
				total: 0,
				label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				elementsByPage: [
					{ id: '25', label: '25' },
					{ id: '50', label: '50' },
					{ id: '100', label: '100' },
				],
			},
		};
	}

	get sort(): ITableSortEvent {
		return { ...this.resetSort() };
	}

	public resetSort(): ITableSortEvent {
		return {
			sortKey: 'created_at',
			sortDirection: 'desc',
			headerIndex: this.header.findIndex((h) => h.sortKey === 'created_at') || 0,
		};
	}

	public transformRow(response: IUsersListBody[]): ITableRow[] {
		return response.map((row) => this.transformListTableRow(row));
	}

	public transformListTableRow(row: IUsersListBody): ITableRow {
		const cells: ITableCell[] = [
			{ label: [row.name, row.surname].filter(Boolean).join(' ').trim() || row.email },
			{ label: row.email },
			{
				label: '',
				tag: {
					label: row?.role
						? this.trans.instant(`MASTER.ROLES.${row?.role?.toUpperCase()}`)
						: notAvailableLabel(this.trans),
					status: 'default',
				},
			},
			{ label: this.getCompanyLabel(row) },
		];

		if (this.canViewLastAccess()) {
			cells.push({
				label: row.last_access_at ? formatDate(row.last_access_at) : '-',
				align: 'right',
			});
		}

		cells.push({ label: formatDate(row.created_at), align: 'right' });

		// Misma columna de acciones en todas las filas (evita hueco); lápiz solo si editable.
		if (this.rolePermissions.canEditUsers() !== false) {
			cells.push(
				this.canShowEditAction(row)
					? {
							label: '',
							tooltip: this.trans.instant('USERS.MENU.EDIT_USER'),
							buttonIcon: {
								id: RowItemAction.EDIT,
								icon: 'icon-edit',
							},
							align: 'right',
						}
					: { label: ' ' },
			);
		}

		return {
			clickValue: row.uuid,
			cells,
		};
	}

	/** No-superadmin no puede editar SUPERADMIN (API lo bloquea). */
	public canShowEditAction(row: IUsersListBody | undefined): boolean {
		if (!row || this.rolePermissions.canEditUsers() === false) {
			return false;
		}
		if (this.rolePermissions.isSuperadmin()) {
			return true;
		}
		return (row.role ?? '').toLowerCase() !== UserRole.SUPERADMIN;
	}

	/** Superadmin y Admin ven la columna último acceso (NIF-957). */
	public canViewLastAccess(): boolean {
		return this.rolePermissions.isSuperadmin() || this.rolePermissions.isAdmin();
	}

	private getCompanyLabel(row: IUsersListBody): string {
		if (!Array.isArray(row?.companies) || row.companies.length === 0) return notAvailableLabel(this.trans);
		return row.companies.filter(Boolean).join(', ');
	}

	public openAccountManageUsers(): void {
		// Abre una pestaña nueva Tecalis account en donde se gestionan los usuarios de Identity
		const url = `${environment.billingUrl}/backoffice/`;
		window.open(url, '_blank');
	}
}
